import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	type SelectProps,
	Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import {
	type FieldValues,
	type Path,
	type UseControllerProps,
	useController,
} from "react-hook-form";

const StyledSelect = styled(Select)(({ theme }) => ({
	"&.Mui-focused": {
		borderColor: theme.palette.background.paper,
	},
	"&.Mui-error .MuiOutlinedInput-notchedOutline": {
		borderColor: theme.palette.error.main,
	},
}));

export type SelectItem<T extends string = string, M = undefined> = {
	value: T;
	title: string;
	meta?: M;
};

export type SelectFieldProps<
	T extends FieldValues,
	P extends Path<T>,
	I extends string = string,
	M = undefined,
> = Omit<SelectProps, "name" | "defaultValue"> &
	UseControllerProps<T, P> & {
		items: string[] | SelectItem<I, M>[];
		itemValue?: "value";
		itemTitle?: "title";
		text?: string;
		isDimmed?: boolean;
		renderOption?: (item: SelectItem<I, M>) => React.ReactNode;
	};

export default function SelectField<
	T extends FieldValues,
	P extends Path<T>,
	I extends string = string,
	M = undefined,
>({
	name,
	control,
	defaultValue,
	rules,
	items,
	itemValue,
	itemTitle,
	label,
	variant = "outlined",
	isDimmed = false,
	renderOption,
	...props
}: SelectFieldProps<T, P, I, M>) {
	const {
		field: { ref, onChange: formOnChange, value: formValue, name: fieldName },
		fieldState: { error },
	} = useController({ name, control, defaultValue, rules });

	const toLabel = (val: unknown) => {
		if (typeof items[0] === "string") return String(val);
		const vKey = itemValue ?? "value";
		const tKey = itemTitle ?? "title";
		return (
			(items as Record<string, unknown>[]).find((it) => it[vKey] === val)?.[
				tKey
			] ?? String(val)
		);
	};

	const renderSelected = (selected: unknown) => {
		if (typeof selected !== "object") {
			return (
				<Typography variant="body2">{toLabel(selected) as string}</Typography>
			);
		}

		const joined = (selected as unknown[]).map(toLabel).join(", ");

		return (
			<Typography
				variant="body2"
				sx={{
					width: "100%",
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
					display: "block",
				}}
			>
				{joined}
			</Typography>
		);
	};

	return (
		<FormControl fullWidth error={!!error}>
			<InputLabel id={`${fieldName}-label`} error={!!error}>
				{label}
			</InputLabel>
			<StyledSelect
				labelId={`${fieldName}-label`}
				id={fieldName}
				name={fieldName}
				value={formValue ?? ""}
				label={label}
				onChange={(e) => formOnChange(e.target.value)}
				inputRef={ref}
				renderValue={renderSelected}
				error={!!error}
				{...props}
			>
				{items.map((item) => {
					if (typeof item === "string") {
						return (
							<MenuItem key={item} value={item}>
								<Typography variant="body2">{item}</Typography>
							</MenuItem>
						);
					}
					const valKey = itemValue ?? "value";
					const titleKey = itemTitle ?? "title";
					const val = item[valKey];
					const title = item[titleKey];
					return (
						<MenuItem key={String(val)} value={val} tabIndex={-1}>
							{renderOption ? (
								renderOption(item)
							) : (
								<Typography variant="body2">{title}</Typography>
							)}
						</MenuItem>
					);
				})}
			</StyledSelect>
			{error && (
				<Typography
					variant="caption"
					color="error"
					sx={{ mt: 0.5, mx: "14px" }}
				>
					{error.message}
				</Typography>
			)}
		</FormControl>
	);
}
